import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckSquare, AlertTriangle, CheckCircle2, Flame, ArrowRight } from "lucide-react";

/**
 * TaskWidgets — dashboard tiles surfacing task-management state.
 *
 * DEV-004C: KPI counters (Due Today / Overdue / Critical Open /
 * Completed Today) now come from the canonical
 * `GET /api/engineers/me/workload` endpoint via the `metrics` prop from
 * `Installer.jsx`. Both Web and Mobile consume the same numbers so no
 * client-side day/UTC math is needed here. `engineerId` is still used to
 * scope the drill-down links.
 *
 * DEV-007: The embedded Recent Activity feed was removed — the Activity
 * Timeline is now Operations-only. Engineer/Customer notifications are
 * unaffected (see the notification bell in the shell).
 */
const TILES = [
    { key: "dueToday",        scope: "due_today", label: "Due today",       icon: CheckSquare,   colour: "#3E9E2E", bg: "#DCFCE7", fg: "#166534" },
    { key: "overdue",         scope: "overdue",   label: "Overdue",         icon: AlertTriangle, colour: "#DA3535", bg: "#FEE2E2", fg: "#991B1B" },
    { key: "criticalOpen",    scope: "all",       label: "Critical open",   icon: Flame,         colour: "#F97316", bg: "#FEF3C7", fg: "#92400E" },
    { key: "completedToday",  scope: "completed", label: "Completed today", icon: CheckCircle2,  colour: "#3B82F6", bg: "#DBEAFE", fg: "#1E40AF" },
];

export default function TaskWidgets({ engineerId = null, metrics = null }) {
    const navigate = useNavigate();
    const engQuery = engineerId ? `&engineer_id=${engineerId}` : "";

    // Testids still use the underscore form to preserve external contracts.
    const TESTID_KEYS = {
        dueToday: "due_today",
        overdue: "overdue",
        criticalOpen: "critical_open",
        completedToday: "completed_today",
    };

    return (
        <section className="mt-10 mb-4" data-testid="task-widgets">
            <div className="mb-4">
                <div className="text-xl md:text-2xl text-[#131B2F] font-medium tracking-tight">Task management</div>
                <div className="text-sm text-[#4B5565] mt-1">Your open work at a glance</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {TILES.map((tile) => {
                    const Icon = tile.icon;
                    const value = metrics ? (metrics[tile.key] ?? 0) : "—";
                    const priority = tile.key === "criticalOpen" ? "&priority=Critical" : "";
                    return (
                        <button
                            key={tile.key}
                            onClick={() => navigate(`/installer/tasks?scope=${tile.scope}${priority}${engQuery}`)}
                            data-testid={`task-widget-${TESTID_KEYS[tile.key]}`}
                            className="text-left bg-white rounded-2xl border border-[#E5E7EB] p-4 md:p-5 hover:border-[#131B2F]/30 hover:shadow-sm transition-all"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: tile.bg, color: tile.fg }}>
                                    <Icon size={18} strokeWidth={1.75} />
                                </div>
                                <ArrowRight size={16} strokeWidth={1.75} className="text-[#4B5565]" />
                            </div>
                            <div className="mt-3 text-2xl md:text-3xl font-medium text-[#131B2F]">{value}</div>
                            <div className="text-sm text-[#4B5565] mt-0.5">{tile.label}</div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
